/**
 * Langflow Chat Widget
 *
 * Usage (defer recommended for performance):
 * <script
 *   defer
 *   src="https://your-domain.com/chat-widget.js"
 *   data-widget-id="your-unique-widget-id"
 *   data-api-url="https://your-api.com/api/chat"
 * ></script>
 *
 * Required attributes:
 * - data-widget-id: Unique widget identifier (UUID)
 * - data-api-url: API endpoint URL for chat
 *
 * Optional attributes:
 * - data-position: Widget position (default: bottom-right)
 * - data-color: Primary color hex (default: #3b82f6)
 * - data-title: Widget title (default: AI Assistant)
 *
 * Note: Use 'defer' for optimal loading without blocking page render
 */

(function (window, document) {
  "use strict";

  const ManagerChatWidget = {
    config: {
      widgetId: null,
      apiUrl: null,
      apiKey: null,
      monitorUrl: null,
      position: "bottom-right",
      title: "Ask AI Assistant Amina",
      color: "#3b82f6",
      sessionId: null,
    },

    isOpen: false,
    isMinimized: false,
    isFullscreen: false,
    messages: [],
    isLoading: false,

    // Polling state
    pollingIntervalId: null,
    lastMessageTimestamp: null,
    displayedMessageIds: new Set(),
    POLLING_INTERVAL: 5000, // 5秒

    getConfigFromScript: function () {
      let currentScript = null;

      if (document.currentScript) {
        currentScript = document.currentScript;
      } else {
        const scripts = document.getElementsByTagName("script");
        for (let i = 0; i < scripts.length; i++) {
          if (scripts[i].src && scripts[i].src.includes("chat-widget.js")) {
            currentScript = scripts[i];
            break;
          }
        }
      }

      if (currentScript && currentScript.dataset) {
        const config = {};
        if (currentScript.dataset.widgetId)
          config.widgetId = currentScript.dataset.widgetId;
        if (currentScript.dataset.apiUrl)
          config.apiUrl = currentScript.dataset.apiUrl;
        if (currentScript.dataset.apiKey)
          config.apiKey = currentScript.dataset.apiKey;
        if (currentScript.dataset.monitorUrl)
          config.monitorUrl = currentScript.dataset.monitorUrl;
        if (currentScript.dataset.position)
          config.position = currentScript.dataset.position;
        if (currentScript.dataset.color)
          config.color = currentScript.dataset.color;
        if (currentScript.dataset.title)
          config.title = currentScript.dataset.title;
        if (currentScript.dataset.aiAvatar)
          config.aiAvatar = currentScript.dataset.aiAvatar;
        // Also check global variable for AI avatar (set by theme)
        if (window.CHAT_AI_AVATAR)
          config.aiAvatar = window.CHAT_AI_AVATAR;
        // Check global variable for welcome image
        if (window.CHAT_WELCOME_IMAGE)
          config.welcomeImage = window.CHAT_WELCOME_IMAGE;
        return config;
      }
      return {};
    },

    init: function (options = {}) {
      const scriptConfig = this.getConfigFromScript();
      this.config = { ...this.config, ...scriptConfig, ...options };

      if (!this.config.widgetId) {
        console.error("[Widget] Error: data-widget-id is required");
        return;
      }
      if (!this.config.apiUrl) {
        console.error("[Widget] Error: data-api-url is required");
        return;
      }

      this.config.sessionId = this.getOrCreateSessionId();

      // Set lastMessageTimestamp to now to prevent loading past messages
      this.lastMessageTimestamp = new Date().toISOString();

      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => this.render());
      } else {
        this.render();
      }
    },

    getOrCreateSessionId: function () {
      const storageKey = `chat_session_${this.config.widgetId}`;
      let sessionId = localStorage.getItem(storageKey);
      if (!sessionId) {
        sessionId = crypto.randomUUID();
        localStorage.setItem(storageKey, sessionId);
      }
      return sessionId;
    },

    render: function () {
      this.injectStyles();
      const container = document.createElement("div");
      container.id = "manager-chat-widget";
      container.className = "mcw-container";
      container.innerHTML = this.getWidgetHTML();
      document.body.appendChild(container);
      this.attachEventListeners();
      this.loadChatHistory();
    },

    getWidgetHTML: function () {
      const positionClass = "mcw-" + this.config.position;
      return `
        <button id="mcw-toggle-btn" class="mcw-toggle-btn ${positionClass}" aria-label="Open chat">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
        </button>
        <div id="mcw-chat-window" class="mcw-chat-window ${positionClass}" style="display: none;">
          <div class="mcw-header ${this.config.aiAvatar ? 'mcw-header-with-avatar' : ''}">
            <div class="mcw-header-left">
              ${this.config.aiAvatar ? `<img src="${this.config.aiAvatar}" alt="AI" class="mcw-header-avatar">` : ''}
              <div class="mcw-header-info">
                <h3 class="mcw-header-title">${this.config.title}</h3>
              </div>
            </div>
            <div class="mcw-header-actions">
              <button id="mcw-fullscreen-btn" class="mcw-icon-btn" aria-label="Fullscreen">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
                </svg>
              </button>
              <button id="mcw-minimize-btn" class="mcw-icon-btn" aria-label="Minimize">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="4 14 10 14 10 20"></polyline>
                  <polyline points="20 10 14 10 14 4"></polyline>
                  <line x1="14" y1="10" x2="21" y2="3"></line>
                  <line x1="3" y1="21" x2="10" y2="14"></line>
                </svg>
              </button>
              <button id="mcw-close-btn" class="mcw-icon-btn" aria-label="Close">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          </div>
          <div id="mcw-messages" class="mcw-messages">
            ${this.config.welcomeImage ? `
            <div class="mcw-fullbody-intro">
              <img src="${this.config.welcomeImage}" alt="AI Assistant" class="mcw-welcome-fullbody">
              <div class="mcw-intro-bubble">Amina here! I'm your AI guide for beauty & health. 🌿 Keep in mind I'm still training and not 100% accurate. Chats are monitored by our team.</div>
            </div>
            ` : ''}
            ${!this.config.welcomeImage && !this.config.aiAvatar ? `
            <div class="mcw-welcome">
              <div class="mcw-welcome-icon">👋</div>
              <h4>Welcome to ${this.config.title}</h4>
              <p>How can I help you today?</p>
            </div>
            ` : ''}
          </div>
          <div class="mcw-input-wrapper">
            <div id="mcw-input-form" class="mcw-input-form">
              <textarea id="mcw-input" class="mcw-input" placeholder="Type your message..." rows="1" enterkeyhint="send" autocomplete="off" autocorrect="off" autocapitalize="sentences" inputmode="text"></textarea>
              <button type="button" id="mcw-send-btn" class="mcw-send-btn" aria-label="Send message">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
              </button>
            </div>
          </div>
        </div>
      `;
    },

    // Custom css
    injectStyles: function () {
      const style = document.createElement("style");
      style.textContent = `
        html, body {
          overflow-x: hidden !important;
          max-width: 100% !important;
        }
        .mcw-container {
          --mcw-primary: ${this.config.color};
          --mcw-primary-dark: ${this.adjustColor(this.config.color, -20)};
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
        }
        .mcw-container *, .mcw-container *::before, .mcw-container *::after {
          box-sizing: border-box;
        }
        .mcw-toggle-btn {
          position: fixed;
          z-index: 9998;
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: var(--mcw-primary);
          color: white;
          border: none;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .mcw-toggle-btn:hover {
          transform: scale(1.1);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
        }
        .mcw-bottom-right { bottom: 20px; right: 20px; }
        .mcw-bottom-left { bottom: 20px; left: 20px; }
        .mcw-top-right { top: 20px; right: 20px; }
        .mcw-top-left { top: 20px; left: 20px; }
        .mcw-chat-window {
          position: fixed;
          z-index: 9999;
          width: calc(100% - 20px);
          max-width: 400px;
          height: 600px;
          max-height: 80%;
          background: white;
          border-radius: 12px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          transition: all 0.3s ease;
        }
        .mcw-chat-window.mcw-minimized { height: 56px; }
        .mcw-chat-window.mcw-fullscreen {
          width: 100% !important;
          height: 100% !important;
          max-width: 100% !important;
          max-height: 100% !important;
          border-radius: 0 !important;
          bottom: 0 !important;
          right: 0 !important;
          left: 0 !important;
          top: 0 !important;
        }
        .mcw-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          background: var(--mcw-primary);
          color: white;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        .mcw-header-left { display: flex; align-items: center; gap: 8px; }
        .mcw-header-icon {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.2);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .mcw-header-title { margin: 0; font-size: 16px; font-weight: 600; color: white; }
        .mcw-header-subtitle { margin: 4px 0 0 0; font-size: 12px; color: rgba(255,255,255,0.8); }
        .mcw-header-with-avatar { padding: 16px; }
        .mcw-header-with-avatar .mcw-header-left { gap: 12px; }
        .mcw-header-avatar {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          object-fit: cover;
          border: 2px solid rgba(255,255,255,0.3);
          flex-shrink: 0;
        }
        .mcw-header-info { display: flex; flex-direction: column; }
        .mcw-header-actions { display: flex; gap: 4px; align-items: flex-start; }
        .mcw-icon-btn {
          width: 32px;
          height: 32px;
          border: none;
          background: rgba(255, 255, 255, 0.1);
          color: white;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s;
        }
        .mcw-icon-btn:hover { background: rgba(255, 255, 255, 0.2); }
        .mcw-messages {
          flex: 1;
          overflow-y: auto;
          padding: 16px 16px 8px 16px;
          background: #f9fafb;
        }
        .mcw-fullbody-intro {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 16px;
        }
        .mcw-welcome-fullbody {
          max-width: 200px;
          max-height: 270px;
          object-fit: contain;
          flex-shrink: 0;
        }
        .mcw-intro-bubble {
          background: white;
          padding: 12px 16px;
          border-radius: 18px;
          border-top-left-radius: 4px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          font-size: 14px;
          color: #374151;
          margin-top: 20px;
          position: relative;
        }
        .mcw-welcome { text-align: center; padding: 32px 16px; }
        .mcw-welcome-icon { font-size: 48px; margin-bottom: 16px; }
        .mcw-welcome-avatar { width: 100px; height: 100px; border-radius: 50%; object-fit: cover; }
        .mcw-welcome h4 { margin: 0 0 8px 0; font-size: 18px; color: #111827; }
        .mcw-welcome p { margin: 0; color: #6b7280; font-size: 14px; }
        .mcw-message { margin-bottom: 12px; display: flex; gap: 8px; }
        .mcw-message-user { flex-direction: row-reverse; }
        .mcw-message-avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: var(--mcw-primary);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: 600;
          flex-shrink: 0;
        }
        .mcw-message-user .mcw-message-avatar { background: #e5e7eb; color: #374151; }
        .mcw-message-content {
          max-width: 70%;
          padding: 10px 14px;
          border-radius: 12px;
          background: white;
          color: #111827;
          font-size: 14px;
          line-height: 1.5;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }
        .mcw-message-user .mcw-message-content { background: var(--mcw-primary); color: white; margin-left: auto; }
        .mcw-message-time { font-size: 11px; color: #9ca3af; margin-top: 4px; }
        .mcw-typing { display: flex; gap: 4px; padding: 10px 14px; }
        .mcw-typing span {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #9ca3af;
          animation: mcw-typing 1.4s infinite;
        }
        .mcw-typing span:nth-child(2) { animation-delay: 0.2s; }
        .mcw-typing span:nth-child(3) { animation-delay: 0.4s; }
        @keyframes mcw-typing {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.7; }
          30% { transform: translateY(-10px); opacity: 1; }
        }
        .mcw-input-wrapper {
          padding: 8px 16px;
          padding-bottom: calc(8px + env(safe-area-inset-bottom, 0px));
          background: white;
          border-top: 1px solid #e5e7eb;
        }
        @media screen and (max-width: 500px) {
          .mcw-chat-window {
            bottom: 10px !important;
            right: 10px !important;
            height: 70vh !important;
            max-height: 70vh !important;
          }
          .mcw-input-wrapper {
            padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
          }
        }
        .mcw-input-form { display: flex; gap: 8px; align-items: flex-end; }
        .mcw-input {
          flex: 1;
          padding: 10px 12px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 16px;
          resize: none;
          max-height: 120px;
          font-family: inherit;
          outline: none;
        }
        .mcw-input:focus {
          border-color: var(--mcw-primary);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        .mcw-send-btn {
          width: 40px;
          height: 40px;
          border: none;
          background: var(--mcw-primary);
          color: white;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s;
          flex-shrink: 0;
        }
        .mcw-send-btn:hover { background: var(--mcw-primary-dark); }
        .mcw-send-btn:disabled { opacity: 0.5; cursor: not-allowed; }
      `;
      document.head.appendChild(style);
    },

    //  Hex color helper
    adjustColor: function (color, percent) {
      if (!color) return "#3b82f6";
      const num = parseInt(color.replace("#", ""), 16);
      const amt = Math.round(2.55 * percent);
      const R = (num >> 16) + amt;
      const G = ((num >> 8) & 0x00ff) + amt;
      const B = (num & 0x0000ff) + amt;
      return (
        "#" +
        (
          0x1000000 +
          (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
          (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
          (B < 255 ? (B < 1 ? 0 : B) : 255)
        )
          .toString(16)
          .slice(1)
      );
    },

    attachEventListeners: function () {
      const toggleBtn = document.getElementById("mcw-toggle-btn");
      const closeBtn = document.getElementById("mcw-close-btn");
      const minimizeBtn = document.getElementById("mcw-minimize-btn");
      const fullscreenBtn = document.getElementById("mcw-fullscreen-btn");
      const sendBtn = document.getElementById("mcw-send-btn");
      const input = document.getElementById("mcw-input");

      toggleBtn.addEventListener("click", () => this.toggleChat());
      closeBtn.addEventListener("click", () => this.closeChat());
      if (minimizeBtn) minimizeBtn.addEventListener("click", () => this.minimizeChat());
      if (fullscreenBtn) fullscreenBtn.addEventListener("click", () => this.toggleFullscreen());
      sendBtn.addEventListener("click", () => this.handleSubmit());

      input.addEventListener("input", function () {
        this.style.height = "auto";
        this.style.height = Math.min(this.scrollHeight, 120) + "px";
      });

      input.addEventListener("keydown", (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          this.handleSubmit();
        }
      });

      // Stop polling when page unloads
      window.addEventListener("beforeunload", () => this.stopPolling());
    },

    toggleChat: function () {
      const chatWindow = document.getElementById("mcw-chat-window");
      const toggleBtn = document.getElementById("mcw-toggle-btn");
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        chatWindow.style.display = "flex";
        toggleBtn.style.display = "none";
        document.getElementById("mcw-input").focus();
        this.startPolling();
      } else {
        chatWindow.style.display = "none";
        toggleBtn.style.display = "flex";
        this.stopPolling();
      }
    },

    closeChat: function () {
      const chatWindow = document.getElementById("mcw-chat-window");
      const toggleBtn = document.getElementById("mcw-toggle-btn");
      this.isOpen = false;
      this.isMinimized = false;
      chatWindow.style.display = "none";
      chatWindow.classList.remove("mcw-minimized");
      toggleBtn.style.display = "flex";
      this.stopPolling();
    },

    minimizeChat: function () {
      const chatWindow = document.getElementById("mcw-chat-window");
      this.isMinimized = !this.isMinimized;
      if (this.isMinimized) {
        chatWindow.classList.add("mcw-minimized");
      } else {
        chatWindow.classList.remove("mcw-minimized");
      }
    },

    toggleFullscreen: function () {
      const chatWindow = document.getElementById("mcw-chat-window");
      this.isFullscreen = !this.isFullscreen;
      if (this.isFullscreen) {
        chatWindow.classList.add("mcw-fullscreen");
      } else {
        chatWindow.classList.remove("mcw-fullscreen");
      }
    },

    handleSubmit: function () {
      const input = document.getElementById("mcw-input");
      const message = input.value.trim();
      if (!message || this.isLoading) return;
      this.addMessage("Customer", message);
      input.value = "";
      input.style.height = "auto";
      this.sendMessage(message);
    },

    addMessage: function (role, content) {
      // Remove mentions like "@xxxxx " from the beginning of messages
      content = content.replace(/^@\S+\s+/g, '');

      const messagesContainer = document.getElementById("mcw-messages");
      const welcome = messagesContainer.querySelector(".mcw-welcome");
      if (welcome) welcome.remove();

      const messageDiv = document.createElement("div");
      messageDiv.className =
        "mcw-message" + (role === "Customer" ? " mcw-message-user" : "");

      let avatar = "🤖";
      let isCustomAvatar = false;
      if (role === "Customer") {
        avatar = "👤";
      } else if (role === "AI") {
        if (this.config.aiAvatar) {
          avatar = this.config.aiAvatar;
          isCustomAvatar = true;
        } else {
          avatar = "🤖";
        }
      } else if (role === "Operator") {
        avatar = "💁";
      }
      const time = new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });

      const avatarHtml = isCustomAvatar
        ? `<img src="${avatar}" alt="AI" style="width:100%;height:100%;object-fit:cover;border-radius:50%;">`
        : avatar;

      messageDiv.innerHTML = `
        <div class="mcw-message-avatar">${avatarHtml}</div>
        <div>
          <div class="mcw-message-content">${this.escapeHtml(content).replace(/\n/g, '<br>')}</div>
          <div class="mcw-message-time">${time}</div>
        </div>
      `;

      messagesContainer.appendChild(messageDiv);
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
      this.messages.push({ role, content, timestamp: new Date() });
    },

    showTyping: function () {
      const messagesContainer = document.getElementById("mcw-messages");
      const typingDiv = document.createElement("div");
      typingDiv.id = "mcw-typing-indicator";
      typingDiv.className = "mcw-message";
      typingDiv.innerHTML = `
        <div class="mcw-message-avatar">Agent</div>
        <div class="mcw-message-content mcw-typing">
          <span></span>
          <span></span>
          <span></span>
        </div>
      `;
      messagesContainer.appendChild(typingDiv);
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    },

    hideTyping: function () {
      const typingIndicator = document.getElementById("mcw-typing-indicator");
      if (typingIndicator) typingIndicator.remove();
    },

    sendMessage: async function (message) {
      this.isLoading = true;
      this.showTyping();

      try {
        const headers = { "Content-Type": "application/json" };
        if (this.config.apiKey) {
          headers["x-api-key"] = this.config.apiKey;
        }

        const response = await fetch(this.config.apiUrl, {
          method: "POST",
          headers: headers,
          body: JSON.stringify({
            input_value: message,
            output_type: 'chat',
            input_type: 'chat',
            session_id: this.config.sessionId,
            tweaks : {
              "Chat Input": {
                sender: "Customer",
                sender_name: "Customer"
              }
            }
          }),
        });

        if (!response.ok) throw new Error("Network response was not ok");

        const data = await response.json();
        console.log("[Widget] sendMessage response:", data);
        // hideTyping is called in pollMessages when assistant message arrives

        if (data.error) {
          this.hideTyping();
          this.addMessage(
            "AI",
            "Sorry, I encountered an error. Please try again."
          );
        //} else {
        //  this.addMessage(
        //    "assistant",
        //    data.response || data.message || "No response"
        //  );
        }

        // Start polling after successful message send
        if (!this.pollingIntervalId) {
          this.startPolling();
        }
      } catch (error) {
        console.error("Chat error:", error);
        this.hideTyping();
        this.addMessage(
          "AI",
          "Sorry, I'm having trouble connecting. Please try again later."
        );
      } finally {
        this.isLoading = false;
      }
    },

    loadChatHistory: async function () {
      //TODO: Implement loading chat history for authenticated users if needed
    },

    // ================================
    // Polling Functions
    // ================================
    startPolling: function () {
      if (this.pollingIntervalId) {
        console.log("[Widget] Polling already running");
        return;
      }
      console.log("[Widget] Starting polling...");
      this.pollMessages();
      this.pollingIntervalId = setInterval(() => {
        this.pollMessages();
      }, this.POLLING_INTERVAL);
    },

    stopPolling: function () {
      if (this.pollingIntervalId) {
        console.log("[Widget] Stopping polling...");
        clearInterval(this.pollingIntervalId);
        this.pollingIntervalId = null;
      }
    },

    pollMessages: async function () {
      if (!this.config.sessionId) {
        console.log("[Widget] No session ID, skipping poll");
        return;
      }

      try {
        // Build monitor URL
        let monitorUrl = `${this.config.monitorUrl}&session_id=${encodeURIComponent(this.config.sessionId)}&order_by=timestamp`;

        // if (this.lastMessageTimestamp) {
        //   monitorUrl += `&after=${encodeURIComponent(this.lastMessageTimestamp)}`;
        // }

        const headers = {};
        if (this.config.apiKey) {
          headers["x-api-key"] = this.config.apiKey;
        }

        const response = await fetch(monitorUrl, {
          method: "GET",
          headers: headers,
        });

        if (!response.ok) {
          console.error("[Widget] Polling error:", response.status);
          return;
        }

        const messages = await response.json();
        console.log("[Widget] pollMessages response:", messages);

        if (Array.isArray(messages) && messages.length > 0) {
          messages.forEach((msg) => {
            // Skip if already displayed
            if (msg.id && this.displayedMessageIds.has(msg.id)) {
              return;
            }

            // Only show assistant messages from polling
            if (msg.sender !== "User" && msg.sender !== "Customer") {
              this.hideTyping();
              this.addMessage(msg.sender, msg.text || msg.message || "");

              if (msg.id) {
                this.displayedMessageIds.add(msg.id);
              }
            }

            // Update last timestamp
            if (msg.timestamp) {
              this.lastMessageTimestamp = msg.timestamp;
            }
          });
        }
      } catch (error) {
        console.error("[Widget] Polling fetch error:", error);
      }
    },

    escapeHtml: function (text) {
      const div = document.createElement("div");
      div.textContent = text;
      return div.innerHTML;
    },
  };

  window.ManagerChatWidget = ManagerChatWidget;

  let initScript = null;
  if (document.currentScript) {
    initScript = document.currentScript;
  } else {
    const scripts = document.getElementsByTagName("script");
    for (let i = 0; i < scripts.length; i++) {
      if (scripts[i].src && scripts[i].src.includes("chat-widget.js")) {
        initScript = scripts[i];
        break;
      }
    }
  }

  if (initScript) {
    if (initScript.dataset && initScript.dataset.autoInit === "false") {
      // Skip auto-init
    } else {
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () =>
          ManagerChatWidget.init()
        );
      } else {
        ManagerChatWidget.init();
      }
    }
  }
})(window, document);
